import type { HttpTransport } from '@mangovoice/api-client'
import type {
  ExtensionInstance,
  ExtensionPreview,
  PaginatedApiResult,
} from '@mangovoice/types'

import { stringifyParams } from './utils'

export interface ExtensionsListParams {
  pbx_id?: number
  type?: string
  limit?: number
  offset?: number
  search?: string
  voicemail_enabled?: boolean
  ordering?: string
  include_voicemail_status?: boolean
  status_filter?: 'active' | 'inactive'
  assigned_user_id?: number[]
  location_id?: number[]
}

/**
 * Row shape of /extensions/select/ (generated `ExtensionSelect` schema).
 * Extends the shared `ExtensionPreview` with the extra fields the select
 * serializer returns; `voicemail_enabled` is only populated when
 * `include_voicemail_status=true` is passed.
 */
export type ExtensionSelectResult = ExtensionPreview & {
  active?: boolean
  voicemail_enabled?: number | null
}

export interface CreateExtensionDialPlanPayload {
  pbx_id: number
  name: string
  number: number | string
  omit_from_directory?: 0 | 1 | boolean
}

export function createExtensionsResource(transport: HttpTransport) {
  return {
    getExtensions: (params?: ExtensionsListParams) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<ExtensionInstance>>(
        qs ? `/app/v1/extensions/?${qs}` : '/app/v1/extensions/',
      )
    },

    /** List extensions (legacy URL without /app/v1 prefix) */
    listLegacy: (params?: {
      pbx_id?: number
      type?: string
      limit?: number
      offset?: number
      search?: string
      ordering?: string
    }) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<ExtensionInstance>>(
        qs ? `/extensions/?${qs}` : '/extensions/',
      )
    },

    getExtensionInfo: (extensionId: number | string) =>
      transport.get<ExtensionInstance>(`/extensions/${extensionId}/`),

    /** List extensions for select dropdowns */
    getAllExtensions: (params?: {
      pbx_id?: number
      type?: string
      limit?: number
      include_voicemail_status?: boolean
    }) => {
      const qs = stringifyParams((params ?? {}) as Record<string, unknown>)
      return transport.get<PaginatedApiResult<ExtensionSelectResult>>(
        qs ? `/extensions/select/?${qs}` : '/extensions/select/',
      )
    },

    /** Get SIP connection info for an extension */
    getSipConnectionInfo: (extensionId: number) =>
      transport.get<unknown>(`/extensions/${extensionId}/sip-connection-info/`),

    /** Get extension lines for a PBX */
    getExtensionLines: (pbxId: number) =>
      transport.get<ExtensionInstance[] | { results: ExtensionInstance[] }>(
        `/app/v1/pbxes/${pbxId}/extension-lines/`,
      ),

    /** Get shared voicemail boxes for a PBX */
    getSharedVoicemailBoxes: (pbxId: number) =>
      transport.get<ExtensionInstance[] | { results: ExtensionInstance[] }>(
        `/app/v1/pbxes/${pbxId}/shared-voicemail-box/`,
      ),

    /** Create a dial plan extension (admin) */
    createExtensionDialPlan: (params: CreateExtensionDialPlanPayload) => {
      const payload = {
        name: params.name,
        number:
          typeof params.number === 'string'
            ? parseInt(params.number, 10)
            : params.number,
        type: 'ExtensionDialPlan',
        omit_from_directory: params.omit_from_directory ? 1 : 0,
      }
      return transport.post<ExtensionInstance>(
        `/app/v1/pbxes/${params.pbx_id}/extensions/`,
        payload,
      )
    },

    /** Create an extension line */
    createLine: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/extension-lines/`,
        data,
      ),

    /** Create a ring group */
    createRingGroup: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/ring-groups/`,
        data,
      ),

    /** Create a call queue */
    createCallQueue: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/call-queues/`,
        data,
      ),

    /** Create a conference room */
    createConferenceRoom: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/conference-rooms/`,
        data,
      ),

    /** Create a virtual fax machine */
    createVirtualFaxMachine: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/virtual-fax-machines/`,
        data,
      ),

    /** Create a shared voicemail box */
    createSharedVoicemailBox: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/shared-voicemail-box/`,
        data,
      ),

    /** Update an extension (generic PATCH) */
    updateExtension: (extensionId: number, data: Record<string, unknown>) =>
      transport.patch<ExtensionInstance>(
        `/app/v1/extensions/${extensionId}/`,
        data,
      ),

    /** Update a ring group (full PUT) */
    updateRingGroup: (
      pbxId: number,
      extensionId: number,
      data: Record<string, unknown>,
    ) =>
      transport.put<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/ring-groups/${extensionId}/`,
        data,
      ),

    /** Update a call queue (full PUT) */
    updateCallQueue: (
      pbxId: number,
      extensionId: number,
      data: Record<string, unknown>,
    ) =>
      transport.put<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/call-queues/${extensionId}/`,
        data,
      ),

    /** Update a conference room (full PUT) */
    updateConferenceRoom: (
      pbxId: number,
      extensionId: number,
      data: Record<string, unknown>,
    ) =>
      transport.put<ExtensionInstance>(
        `/app/v1/pbxes/${pbxId}/conference-rooms/${extensionId}/`,
        data,
      ),

    /** Delete an extension */
    deleteExtension: (extensionId: number) =>
      transport.delete<void>(`/app/v1/extensions/${extensionId}/`),

    /** Delete extension line and reassign its resources */
    deleteAndReassign: (
      pbxId: number,
      extensionId: number,
      data: Record<string, unknown>,
    ) =>
      transport.post<void>(
        `/app/v1/pbxes/${pbxId}/extension-lines/${extensionId}/delete-and-reassign/`,
        data,
      ),

    /** Update user groups for an extension */
    updateUserGroups: (extensionId: number, data: unknown) =>
      transport.put<unknown>(
        `/app/v1/extensions/${extensionId}/user-groups/`,
        data,
      ),

    /** Resend secure fax link */
    resendSecureFaxLink: (extensionId: number) =>
      transport.post<unknown>(
        `/app/v1/extensions/${extensionId}/resend-secure-fax-link/`,
      ),

    /** Bulk update location for extension lines */
    bulkUpdateLocation: (pbxId: number, data: Record<string, unknown>) =>
      transport.post<unknown>(
        `/app/v1/pbxes/${pbxId}/extension-lines/bulk-update-location/`,
        data,
      ),

    /** Mass edit extension lines */
    massEdit: (pbxId: number, data: unknown) =>
      transport.post<unknown>(
        `/app/v1/pbxes/${pbxId}/extension-lines/mass-edit/`,
        data,
      ),

    /** Reset SIP secret for a line extension */
    resetSipSecret: (pbxId: number, extensionId: number) =>
      transport.put<unknown>(
        `/app/v1/pbxes/${pbxId}/extension-lines/${extensionId}/reset-sip-secret/`,
      ),

    /** Clear dial-now for an extension */
    clearDialNow: (extensionId: number) =>
      transport.post<unknown>(
        `/app/v1/extensions/${extensionId}/clear-dial-now/`,
      ),

    /** Get mobile devices for an extension */
    getMobileDevices: (extensionId: number) =>
      transport.get<unknown>(
        `/app/v1/extensions/${extensionId}/mobile-devices/`,
      ),

    /** Set active state for an extension line */
    setActiveState: (
      pbxId: number,
      extensionId: number,
      data: Record<string, unknown>,
    ) =>
      transport.post<unknown>(
        `/app/v1/pbxes/${pbxId}/extension-lines/${extensionId}/set-active-state/`,
        data,
      ),

    /** Get call queue agent status */
    getQueueAgentStatus: (extensionId: number) =>
      transport.get<unknown>(`/extensions/${extensionId}/call-queues/agent/`),

    /** Queue login */
    queueLogin: (extensionId: number) =>
      transport.put<void>(`/extensions/${extensionId}/call-queues/log-in/`),

    /** Queue logout */
    queueLogout: (extensionId: number) =>
      transport.put<void>(`/extensions/${extensionId}/call-queues/log-out/`),
  }
}
